import {
  Box,
  Button,
  IconButton,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { getIsValidURLFormat } from '@prismamedia/one-components';
import React from 'react';
import { actions, ArticleEditAction } from '../../reducer';
import { GetArticleQuery_query_article_Article_bibliographies_ArticleBibliography } from '../../../../__generated__/queries-web';

interface ExternalSourcesInputProps {
  bibliographies: GetArticleQuery_query_article_Article_bibliographies_ArticleBibliography[];
  dispatch: React.Dispatch<ArticleEditAction>;
}

export const ExternalSourcesInput: React.FC<ExternalSourcesInputProps> = ({
  bibliographies,
  dispatch,
}) => {
  const handleAdd = () => {
    const newBibliographies = [
      ...bibliographies,
      {
        id: `temp-${Date.now()}-${Math.random()}`,
        title: '',
        url: '',
      } as GetArticleQuery_query_article_Article_bibliographies_ArticleBibliography,
    ];
    dispatch(
      actions.updateField({
        field: 'bibliographies',
        value: newBibliographies,
      }),
    );
  };

  const handleDelete = (index: number) => {
    const newBibliographies = bibliographies.filter((_, i) => i !== index);
    dispatch(
      actions.updateField({
        field: 'bibliographies',
        value: newBibliographies,
      }),
    );
  };

  const handleTitleChange = (index: number, value: string) => {
    const newBibliographies = bibliographies.map((bib, i) =>
      i === index ? { ...bib, title: value } : bib,
    );
    dispatch(
      actions.updateField({
        field: 'bibliographies',
        value: newBibliographies,
      }),
    );
  };

  const handleUrlChange = (index: number, value: string) => {
    const newBibliographies = bibliographies.map((bib, i) =>
      i === index ? { ...bib, url: value } : bib,
    );
    dispatch(
      actions.updateField({
        field: 'bibliographies',
        value: newBibliographies,
      }),
    );
  };

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <InputLabel shrink>Sources Externes</InputLabel>
        <Button
          variant="outlined"
          size="small"
          onClick={handleAdd}
          sx={{ textTransform: 'none' }}
        >
          + Ajouter
        </Button>
      </Box>
      {bibliographies.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ color: 'text.disabled', opacity: 0.7 }}
        >
          Aucune source externe ajoutée
        </Typography>
      ) : (
        bibliographies.map((bibliography, index) => {
          const hasUrlError = Boolean(
            bibliography.url && !getIsValidURLFormat(bibliography.url),
          );
          return (
            <Box
              key={bibliography.id}
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'flex-start',
                mb: 2,
              }}
            >
              <TextField
                fullWidth
                placeholder="Titre du site / page"
                value={bibliography.title}
                onChange={(e) => handleTitleChange(index, e.target.value)}
                variant="standard"
                helperText={hasUrlError ? '\u00A0' : ''}
                FormHelperTextProps={{
                  sx: { minHeight: hasUrlError ? '1.5rem' : 0, margin: 0 },
                }}
                sx={{ flex: 1 }}
              />
              <TextField
                fullWidth
                placeholder="https://..."
                value={bibliography.url}
                onChange={(e) => handleUrlChange(index, e.target.value)}
                variant="standard"
                error={hasUrlError}
                helperText={
                  hasUrlError ? "Le format de l'URL est invalide" : ''
                }
                FormHelperTextProps={{
                  sx: { minHeight: hasUrlError ? '1.5rem' : 0, margin: 0 },
                }}
                sx={{ flex: 1 }}
              />
              <IconButton
                onClick={() => handleDelete(index)}
                size="small"
                sx={{
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: 'error.light',
                    color: 'error.dark',
                  },
                }}
              >
                <Delete />
              </IconButton>
            </Box>
          );
        })
      )}
    </Box>
  );
};
